import BackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { CircularProgress } from '@mui/material';
import { useSearch } from 'apps/wrapper/src/hooks/use-search/use-search';
import React, { forwardRef, KeyboardEventHandler, LegacyRef, useEffect, useRef, useState } from 'react';
import { IconWrapper, Search, StyledCancelIcon, StyledInputBase } from './search-input.styles';

export interface SearchInputProps {
  value: string;
  ref: LegacyRef<HTMLDivElement>;
  onChange: (value: string) => void;
  onBack?: () => void;
}

export const SearchInput = forwardRef<HTMLDivElement, SearchInputProps>((props, ref) => {
  const { value, onChange, onBack } = props;
  const { isLoading, query, data } = useSearch();
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleClear = () => {
    onChange('');

    if (inputRef.current) inputRef.current.focus();
  };

  const handleFocus = (isFocused: boolean) => () => setFocused(isFocused);

  useEffect(() => {
    if (onBack) inputRef.current?.focus();
  }, [onBack]);

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.code === 'Enter') handleClear();
  };

  return (
    <Search ref={ref} sx={{ border: focused ? '2px solid #1EB9FB' : '2px solid transparent', zIndex: 9999 }}>
      <IconWrapper className={onBack && 'with-pointer-events'}>
        {onBack ? (
          <BackIcon onClick={onBack} sx={{ color: '#1EB9FB' }} />
        ) : (
          <SearchIcon sx={{ color: focused ? '#1EB9FB' : '#D5D9DD' }} />
        )}
      </IconWrapper>
      <StyledInputBase
        inputRef={inputRef}
        onChange={handleChange}
        value={value}
        type="text"
        placeholder="Search for a patient…"
        inputProps={{ 'aria-label': 'search' }}
        onFocus={handleFocus(true)}
        onBlur={handleFocus(false)}
        autoFocus
      />
      <IconWrapper
        sx={{ cursor: 'pointer', pointerEvents: 'all', display: value ? 'flex' : 'none' }}
        onClick={handleClear}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        {query && query.length > 0 && !isLoading && <StyledCancelIcon />}
        {isLoading && data === null && <CircularProgress size={20} sx={{ color: 'white' }} />}
      </IconWrapper>
    </Search>
  );
});
